import { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, CircularProgress, Alert,
  Grid, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Divider,
  IconButton,
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CalendarViewWeek as RosterIcon,
} from '@mui/icons-material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface StaffMember { id: number; full_name: string; email: string; role_name: string | null }
interface ShiftAssignment {
  id: number; user_id: number; shift_date: string; start_time: string; end_time: string;
  status: string; notes: string | null; user_name: string; user_email: string; role_name: string | null;
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function addDays(dateStr: string, n: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
}

function getMondayOf(dateStr: string): string {
  const d = new Date(dateStr);
  const day = d.getDay() === 0 ? 6 : d.getDay() - 1;
  d.setDate(d.getDate() - day);
  return d.toISOString().split('T')[0];
}

export default function RosterPage() {
  const [weekStart, setWeekStart] = useState(() => getMondayOf(new Date().toISOString().split('T')[0]));
  const [assignments, setAssignments] = useState<ShiftAssignment[]>([]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [newDate, setNewDate] = useState('');
  const [newStart, setNewStart] = useState('09:00');
  const [newEnd, setNewEnd] = useState('17:00');
  const [newNotes, setNewNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  async function load() {
    setLoading(true); setError('');
    try {
      const res = await api.get<{ week_start: string; week_end: string; assignments: ShiftAssignment[]; staff: StaffMember[] }>(
        `${API_ENDPOINTS.ROSTER.LIST}?week_start=${weekStart}`,
      );
      setAssignments(res.assignments);
      setStaff(res.staff);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load roster');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [weekStart]);

  const weekDates = DAYS.map((_, i) => addDays(weekStart, i));

  async function handleSave() {
    if (!newUserId || !newDate || !newStart || !newEnd) return;
    setSaving(true); setSaveError('');
    try {
      await api.post(API_ENDPOINTS.ROSTER.CREATE, {
        user_id: parseInt(newUserId),
        shift_date: newDate,
        start_time: newStart,
        end_time: newEnd,
        notes: newNotes || undefined,
      });
      setDialogOpen(false);
      setNewUserId(''); setNewDate(''); setNewNotes('');
      await load();
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm('Delete this shift?')) return;
    try {
      await api.delete(API_ENDPOINTS.ROSTER.DELETE(id));
      await load();
    } catch {
      // ignore
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RosterIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>Staff Roster</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => setWeekStart(addDays(weekStart, -7))}><PrevIcon /></IconButton>
          <Typography variant="body1" fontWeight={500}>{weekStart} — {addDays(weekStart, 6)}</Typography>
          <IconButton onClick={() => setWeekStart(addDays(weekStart, 7))}><NextIcon /></IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setDialogOpen(true); setNewDate(weekStart); }}>
            Add Shift
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <Grid container>
            {/* Header row */}
            {weekDates.map((date, i) => (
              <Grid item xs key={date} sx={{
                borderRight: i < 6 ? '1px solid' : 'none',
                borderColor: 'divider',
                bgcolor: 'grey.50',
                p: 1,
              }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  {DAYS[i]}
                </Typography>
                <Typography variant="body2" fontWeight={600}>{date.slice(5)}</Typography>
              </Grid>
            ))}
            <Divider sx={{ width: '100%', borderColor: 'divider' }} />
            {/* Shift cells */}
            {weekDates.map((date, i) => {
              const dayShifts = assignments.filter(a => a.shift_date === date);
              return (
                <Grid item xs key={date} sx={{
                  borderRight: i < 6 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  p: 1,
                  minHeight: 120,
                  verticalAlign: 'top',
                }}>
                  {dayShifts.length === 0 ? (
                    <Typography variant="caption" color="text.disabled">—</Typography>
                  ) : (
                    dayShifts.map(s => (
                      <Box key={s.id} sx={{ mb: 1, position: 'relative' }}>
                        <Chip
                          size="small"
                          label={s.user_name}
                          color={s.status === 'Scheduled' ? 'primary' : 'default'}
                          sx={{ mb: 0.25, maxWidth: '100%' }}
                        />
                        <Typography variant="caption" display="block" color="text.secondary">
                          {s.start_time}–{s.end_time}
                        </Typography>
                        {s.role_name && (
                          <Typography variant="caption" display="block" color="text.disabled">
                            {s.role_name}
                          </Typography>
                        )}
                        <IconButton size="small" sx={{ position: 'absolute', top: -4, right: -4, opacity: 0.4 }}
                          onClick={() => handleDelete(s.id)}>
                          <DeleteIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    ))
                  )}
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {/* Add Shift Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Shift</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Staff Member</InputLabel>
            <Select value={newUserId} label="Staff Member" onChange={e => setNewUserId(e.target.value)}>
              {staff.map(s => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {s.full_name} {s.role_name ? `(${s.role_name})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField size="small" label="Date" type="date" value={newDate}
            onChange={e => setNewDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" label="Start" type="time" value={newStart}
              onChange={e => setNewStart(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField size="small" label="End" type="time" value={newEnd}
              onChange={e => setNewEnd(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          </Box>
          <TextField size="small" label="Notes" value={newNotes}
            onChange={e => setNewNotes(e.target.value)} multiline rows={2} />
          {saveError && <Alert severity="error">{saveError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !newUserId || !newDate}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
